import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation } from 'wouter';
import { Building2, ShieldCheck, Mail, LogIn, ArrowRight, LogOut, QrCode, X, Package } from 'lucide-react';
import { Html5QrcodeScanner } from 'html5-qrcode';

export default function FacilityLogin() {
  const { user, isStaff, isAdmin, staffInfo, signInWithGoogle, loading, logout } = useAuth();
  const [, setLocation] = useLocation();
  const [isScanning, setIsScanning] = useState(false);
  const [scannedStaffInfo, setScannedStaffInfo] = useState<any>(null);
  const [scannedProductInfo, setScannedProductInfo] = useState<any>(null);

  useEffect(() => {
    if (isScanning) {
      const scanner = new Html5QrcodeScanner(
        "reader",
        { fps: 10, qrbox: { width: 250, height: 250 } },
        /* verbose= */ false
      );

      scanner.render((decodedText) => {
        try {
          const data = JSON.parse(decodedText);
          if (data.type === 'staff_login') {
            setScannedStaffInfo(data);
            setIsScanning(false);
            scanner.clear();
          } else if (data.type === 'product') {
            setScannedProductInfo(data);
            setIsScanning(false);
            scanner.clear();
          }
        } catch (e) {
          console.error("Invalid QR code", e);
        }
      }, (error) => {
        // console.warn(error);
      });

      return () => {
        scanner.clear().catch(e => console.error(e));
      };
    }
  }, [isScanning]);

  useEffect(() => {
    if (user && (isStaff || isAdmin)) {
       // redirect to unit dashboard if already logged in
       setLocation('/unit-dashboard');
    }
  }, [user, isStaff, isAdmin, setLocation]);

  const handleLogin = async () => {
    try {
      await signInWithGoogle();
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') return;
      console.error(error);
      alert('Login failed');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#fdfbf6] flex flex-col items-center justify-center p-6 font-sans relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none">
         <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-red-900 blur-3xl"></div>
         <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-[#1a2e16] blur-3xl"></div>
      </div>

      <div className="max-w-md w-full bg-white border border-slate-200/60 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.15)] rounded-3xl overflow-hidden animate-in fade-in slide-in-from-bottom-8 duration-1000 relative z-10">
        <div className="bg-[#1a2e16] p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
            <div className="relative z-10">
              <div className="inline-flex w-20 h-20 bg-white rounded-2xl items-center justify-center mb-6 shadow-2xl rotate-3 group hover:rotate-0 transition-transform duration-500">
                <Package className="w-10 h-10 text-red-900" />
              </div>
              <h1 className="text-3xl font-serif font-black text-white leading-tight">Apothecary<br /><span className="text-emerald-400">Operations</span></h1>
              <div className="flex items-center justify-center gap-3 mt-4">
                 <div className="h-px w-8 bg-white/20"></div>
                 <p className="text-white/40 text-[9px] uppercase tracking-[0.4em] font-black">Facility Centre Portal</p>
                 <div className="h-px w-8 bg-white/20"></div>
              </div>
            </div>
        </div>

        <div className="p-12 text-center">
          <p className="text-slate-500 mb-10 leading-relaxed text-sm font-medium">
            Authorized personnel only. Access operational distribution logs, regional fulfillment processing, and unit inventory control.
          </p>

          {user ? (
            <div className="space-y-6">
              {scannedStaffInfo && user.email !== scannedStaffInfo.email && (
                 <div className="p-5 bg-red-50 border border-red-100 text-red-900 text-xs text-left mb-6 rounded-xl">
                    <p className="font-black uppercase tracking-widest text-[9px] mb-2 flex items-center gap-2">
                       <X className="w-3 h-3" /> Identity Conflict
                    </p>
                    <p className="font-bold opacity-80">Device ID: {scannedStaffInfo.email}</p>
                    <p className="opacity-60 mt-1">Status: Active session mismatch detected.</p>
                 </div>
              )}
              {!isStaff && !isAdmin ? (
                <div className="p-6 bg-red-50 border border-red-100 text-red-900 text-left mb-8 rounded-xl ring-4 ring-red-50/50">
                  <div className="flex items-center gap-3 mb-3">
                    <ShieldCheck className="w-5 h-5 text-red-600" />
                    <p className="font-black uppercase tracking-widest text-[10px]">Restricted Domain</p>
                  </div>
                  <p className="text-xs leading-relaxed font-bold opacity-75">Verification failed. <strong>{user.email}</strong> is not recognized as part of the regional operations staff.</p>
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-4">
                <button 
                  onClick={logout}
                  className="w-full bg-[#fdfbf6] border-2 border-slate-100 py-4 rounded-xl font-black uppercase tracking-[0.2em] text-[10px] text-slate-600 hover:bg-slate-50 hover:border-slate-200 transition-all flex items-center justify-center gap-3 active:scale-95"
                >
                  <LogOut className="w-4 h-4 text-slate-400" />
                  Reset Session
                </button>
              </div>

              <Link href="/" className="inline-block text-slate-300 text-[10px] font-black uppercase tracking-[0.3em] hover:text-red-900 transition-all mt-8 border-b border-transparent hover:border-red-900/20 pb-1">
                Return to Surface Web
              </Link>
            </div>
          ) : (
            <div className="space-y-10">
              {isScanning ? (
                <div className="space-y-6">
                  <div id="reader" className="overflow-hidden border-2 border-red-900/5 rounded-2xl shadow-inner bg-slate-50"></div>
                  <button 
                    onClick={() => setIsScanning(false)}
                    className="w-full bg-slate-100 py-4 text-slate-600 font-bold uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 rounded-xl border border-slate-200 hover:bg-white transition-all flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Abort Scanning
                  </button>
                </div>
              ) : (
                <>
                  {scannedStaffInfo && (
                    <div className="p-6 bg-[#fdfbf6] border border-red-900/10 text-left animate-in fade-in zoom-in-95 rounded-2xl shadow-sm ring-1 ring-black/5">
                      <div className="flex items-center justify-between mb-6">
                        <div className="text-[9px] px-3 py-1 bg-red-900 text-white font-black uppercase tracking-widest rounded-full">Device Identified</div>
                        <button onClick={() => setScannedStaffInfo(null)} className="text-slate-400 hover:text-red-900 transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-4 mb-2">
                        <div className="w-12 h-12 bg-white border border-slate-200 flex items-center justify-center text-red-900 font-black text-xl rounded-xl shadow-sm">
                          {scannedStaffInfo.email.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-900 leading-tight">{scannedStaffInfo.email}</p>
                          <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">Terminal: {scannedStaffInfo.facilityId?.slice(-6).toUpperCase()}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-4">
                    <button 
                      onClick={handleLogin}
                      className="group relative w-full bg-red-900 text-white py-6 rounded-2xl flex items-center justify-center gap-3 font-black uppercase tracking-[0.3em] text-[11px] hover:bg-black transition-all shadow-[0_20px_40px_-10px_rgba(153,27,27,0.4)] active:scale-95 overflow-hidden"
                    >
                      <LogIn className="w-4 h-4" />
                      <span>{scannedStaffInfo ? 'Authorize Identity' : 'Encrypted Login'}</span>
                    </button>
                    
                    {!scannedStaffInfo && (
                      <button 
                        onClick={() => setIsScanning(true)}
                        className="w-full border-2 border-slate-100 py-4 font-black uppercase tracking-[0.2em] text-[10px] text-slate-500 hover:bg-white hover:border-slate-200 transition-all flex items-center justify-center gap-3 rounded-2xl active:scale-95"
                      >
                        <QrCode className="w-4 h-4 text-red-900/40" strokeWidth={3} />
                        Scan Terminal ID
                      </button>
                    )}
                  </div>
                </>
              )}
              
              <div className="pt-10 border-t border-slate-100">
                <div className="flex items-center gap-3 justify-center text-slate-300 text-[10px] font-black uppercase tracking-[0.4em] mb-4">
                  <ShieldCheck className="w-3.5 h-3.5 opacity-50" />
                  Protocol Level 5.0
                </div>
                <p className="text-[10px] text-slate-300 leading-relaxed uppercase tracking-[0.1em] font-medium opacity-60 px-4">
                  Secure transmission enabled. All sessions are logged for operational auditing.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-16 text-[10px] uppercase font-black tracking-[0.6em] text-slate-400 opacity-40">
        Apothecary Masala Operations
      </div>
    </div>
  );
}
